<template>
  <div class="notification">
    <!-- Card Header -->
    <div class="card">
      <div class="card-header">
        <i
          v-if="notificationIcon"
          class="icon fa-5x"
          :class="notificationIcon"
        ></i>
        <div class="card-content">
          <div class="card-title">
            <p v-if="item.subject" class="title">{{ item.subject }}</p>
            <span v-if="timeCreatedAt" class="title">{{ timeCreatedAt }}</span>
          </div>
          <div class="card-time-action">
            <p v-if="shortDescription" class="subtitle">
              {{ shortDescription }}
            </p>
            <p
              v-if="!shortDescription"
              class="subtitle"
              v-html="notificationBody"
            ></p>
            <template v-if="shortDescription && isExpanded">
              <button
                class="close-icon"
                v-if="notificationBody"
                @click="isExpanded = !isExpanded"
              >
                <font-awesome-icon :icon="['fa', 'xmark']" class="icon-x" />
              </button>
            </template>
            <template v-else-if="shortDescription">
              <button
                class="view-button"
                v-if="notificationBody"
                @click="isExpanded = !isExpanded"
              >
                <span class="button-text">View</span>
                <span class="icon-box">
                  <font-awesome-icon
                    class="icon-small"
                    icon="arrow-right"
                    size="md"
                  />
                </span>
              </button>
            </template>
          </div>
        </div>
      </div>
      <transition
        @before-enter="beforeEnter"
        @enter="enter"
        @after-enter="afterEnter"
        @before-leave="beforeLeave"
        @leave="leave"
        @after-leave="afterLeave"
      >
        <div v-show="isExpanded" ref="expandable" class="expanded-content">
          <div class="subtitle" v-html="notificationBody"></div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
export default {
  name: "NotificationItem",
  props: {
    item: Object
  },
  data() {
    return {
      isExpanded: false
    };
  },
  computed: {
    shortDescription() {
      const salesforceKey = Object.keys(this.item?.event).find(key =>
        key.startsWith("vpc_")
      );
      if (salesforceKey && this.item?.event[salesforceKey]?.short_description) {
        return this.item?.event[salesforceKey]?.short_description
      } else {
        return null
      }
    },
    dateCreatedAt() {
      return this.item?.createdAt.split("T")[0];
    },
    timeCreatedAt() {
      const createdAt = this.item?.createdAt;
      if (!createdAt) return "";

      const date = new Date(createdAt);
      return date
        .toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
          timeZone: "UTC"
        })
        .replace(" ", "");
    },
    notificationBody() {
      const saleforceFileBody = this.item?.event?.vpc_salesforce_file
        ?.base_64_body;

      return saleforceFileBody
        ? this.decodeData(saleforceFileBody)
        : this.item.body;
    },
    notificationIcon() {
      const salesforceKey = Object.keys(this.item?.event).find(key =>
        key.startsWith("vpc_salesforce")
      );
      const iconValue = salesforceKey
        ? this.item?.event[salesforceKey].icon
        : "fa fa-bell";
      return iconValue;
    }
  },
  methods: {
    decodeData(data) {
      try {
        return atob(data);
      } catch (error) {
        return data;
      }
    },
    beforeEnter(el) {
      el.style.height = "0";
      el.style.opacity = "0";
      el.style.overflow = "hidden";
    },
    enter(el) {
      const height = el.scrollHeight;
      el.style.transition = "none";

      // Force reflow to apply height 0
      void el.offsetHeight;

      el.style.transition = "height 0.4s ease, opacity 0.4s ease";
      el.style.height = height + "px";
      el.style.opacity = "1";
    },
    afterEnter(el) {
      el.style.height = "auto";
      el.style.transition = "";
      el.style.overflow = "";
    },
    beforeLeave(el) {
      el.style.height = el.scrollHeight + "px";
      el.style.opacity = "1";
      el.style.overflow = "hidden";
    },
    leave(el) {
      el.style.transition = "height 0.4s ease, opacity 0.4s ease";

      // Force reflow again
      void el.offsetHeight;

      el.style.height = "0";
      el.style.opacity = "0";
    },
    afterLeave(el) {
      el.style.transition = "";
      el.style.overflow = "";
    }
  }
};
</script>

<style scoped lang="less">
.notification {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin: 8px 0 18px;
}

.card {
  border-radius: 8px;
  padding-inline: 32px;
  border: 1px solid rgba(224, 229, 245, 1);
  padding-block: 19px;
  box-shadow: 0 2px 16px 0 rgba(0, 0, 0, 0.04);
  overflow: hidden;
}

.card-header {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 30px;
  padding: 0;
  background-color: transparent;
  border: none;
}

.card-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.card-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.icon {
  margin: 1px;
  color: #604aff;
}

.title {
  font-weight: 500;
  font-size: 20px;
  line-height: 30px;
  color: #604aff !important;
  margin: 0;
  width: auto;
}

.subtitle {
  font-weight: 400;
  font-size: 22px;
  line-height: 30px;
  color: #000 !important;
  margin: 0 0 10px;
  max-width: 90%;
}

.text-content {
  display: flex;
  width: 100%;
}

.card-time-action {
  display: flex;
  align-items: flex-end;
  gap: 15px;
  justify-content: space-between;
  width: 100%;
}

.time {
  font-size: 0.75rem;
  color: #999;
}

.view-button {
  height: 42px;
  width: 100px;
  flex: 0 0 100px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #604aff;
  color: white;
  font-weight: bold;
  padding: 9px 13px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 1.25rem;
}

.button-text {
  font-size: 1.6rem;
  margin-right: 16px;
}

.icon-box {
  background-color: white;
  padding: 5px 6px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-small {
  width: 11px;
  height: 11px;
  margin: 1px;
  color: #604aff;
}

.icon-x {
  width: 25px;
  height: 25px;
  color: #000;
}

.close-icon {
  height: 42px;
  width: 100px;
  padding: 0;
  background: transparent;
  border: none;
  flex: 0 0 100px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.expanded-content {
  border-top: 2px solid #604aff;
  padding-top: 20px;
  margin-top: 20px;
  font-size: 0.875rem;
  color: #333;
}

.expanded-content ul {
  padding-left: 1rem;
  margin: 0.5rem 0;
}

.expanded-content ul li {
  margin-bottom: 0.4rem;
}
</style>
